import { queryPayload } from '@/lib/payloadClient'
import { createLogger } from '@/lib/logger'
import { PartnershipTier } from '@/collections/App/constants'
import type { App } from '@/payload-types'

const trendingLogger = createLogger('Trending Partners')

/**
 * All platinum/gold partner apps, for the client-side "Trending Right Now" pick.
 *
 * Fetched globally (no per-page exclusion) so it caches once; callers exclude the current
 * page's apps client-side and run `pickWeightedPartner`, which re-rolls on every refresh.
 *
 * Populate shape verified via `pnpm debug:query`: this trims to ~1.9 KB/doc (vs ~222 KB/doc
 * with no select) and resolves icon/glyph URLs (`media: { url, filename }` is required for the
 * computed upload `url`), plus review.bestFor and categoryRecommendations for AppRanking's
 * recommendation/bestFor lookup. depth 2 reaches the ACRs' category.
 */
export async function fetchTrendingPartnerCandidates(): Promise<App[]> {
  try {
    const { docs: appDocs } = await queryPayload({
      collection: 'app',
      // Populate affiliateLink + the link relation so processAffiliateLink resolves the
      // real shortLink (e.g. efficient.link/r/superhuman-mail), epcValue, and
      // passReferrerUrl. Without it, processAffiliateLink falls back to a slug guess
      // (efficient.link/r/<slug>) that's wrong whenever the short link != /r/<slug> and
      // reports EPC as $0. The link fields MUST be listed explicitly — `link: {}` overrides
      // the Link collection's defaultPopulate with an empty object and drops the CTA.
      select: {
        icon: true,
        glyph: true,
        slug: true,
        name: true,
        review: true,
        affiliateLink: true,
        brandPrimaryColor: true,
        brandSecondaryColor: true,
        brandTertiaryColor: true,
        brandBackgroundColor: true,
        categoryRecommendations: true,
        partnershipTier: true,
      },
      populate: {
        app_category_recommendation: {
          category: true,
          classification: true,
          discontinuedAt: true,
          recommendation: true,
          bestFor: true,
        },
        category: { name: true, slug: true, categoryCriteria: true },
        review: { bestFor: true },
        link: { shortLink: true, epcValue: true, passReferrerUrl: true },
        media: {
          url: true,
          alt: true,
          filename: true,
          width: true,
          height: true,
          coveragePercent: true,
          xOffsetPercent: true,
          yOffsetPercent: true,
        },
      },
      where: { partnershipTier: { in: [PartnershipTier.Platinum, PartnershipTier.Gold] } },
      depth: 2,
      pagination: false,
      // -v2: bumped when affiliateLink/link were added to the populate so the deploy reads
      // a fresh key instead of serving the old (no-link) cached shape until TTL.
      cacheKey: 'query:trending-partner-candidates-v2',
    })

    return appDocs as App[]
  } catch (error) {
    trendingLogger.error('Failed to fetch trending partner candidates', error)
    return []
  }
}
